import { useState, useCallback } from 'react';
import { useCategories } from '@/hooks/useCategories';
import { api } from '@/lib/api';
import type { CreateCategoryRequest, UpdateCategoryRequest } from '@photobooth/types';

export function CategoriesPage() {
  const { categories, isLoading, error, refetch } = useCategories();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', isPublic: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState({ name: '', description: '', isPublic: true });
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data: CreateCategoryRequest = {
        name: formData.name,
        description: formData.description || undefined,
        isPublic: formData.isPublic,
        displayOrder: categories.length,
      };
      await api.categories.create(data);
      setFormData({ name: '', description: '', isPublic: true });
      setShowForm(false);
      refetch();
    } catch (err) {
      alert('Failed to create category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.categories.delete(categoryId);
      refetch();
    } catch (err) {
      alert('Failed to delete category');
    }
  };

  const handleEditClick = (category: { categoryId: string; name: string; description?: string; isPublic?: boolean }) => {
    setEditingCategoryId(category.categoryId);
    setEditFormData({ name: category.name, description: category.description || '', isPublic: category.isPublic ?? true });
  };

  const handleUpdate = async (e: React.FormEvent, categoryId: string) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data: UpdateCategoryRequest = {
        name: editFormData.name,
        description: editFormData.description || undefined,
        isPublic: editFormData.isPublic,
      };
      const response = await api.categories.update(categoryId, data);
      if (response.success) {
        setEditingCategoryId(null);
        refetch();
      } else {
        alert('Failed to update category');
      }
    } catch (err) {
      alert('Failed to update category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCategoryId(null);
  };

  const handleDragStart = useCallback((e: React.DragEvent, categoryId: string) => {
    setDraggedId(categoryId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', categoryId);
    e.dataTransfer.setData('application/json', JSON.stringify({ categoryId }));
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, categoryId: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (draggedId && draggedId !== categoryId) {
        setDragOverId(categoryId);
      }
    },
    [draggedId]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent, dropTargetId: string) => {
      e.preventDefault();
      setDragOverId(null);
      setDraggedId(null);

      const dragged = e.dataTransfer.getData('text/plain');
      if (!dragged || dragged === dropTargetId) return;

      const oldIndex = categories.findIndex((c) => c.categoryId === dragged);
      const newIndex = categories.findIndex((c) => c.categoryId === dropTargetId);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...categories];
      const [moved] = reordered.splice(oldIndex, 1);
      if (!moved) {
        return;
      }
      reordered.splice(newIndex, 0, moved);

      setIsSubmitting(true);
      try {
        const updates = reordered
          .map((cat, index) => ({ cat, newOrder: index }))
          .filter(({ cat, newOrder }) => cat.displayOrder !== newOrder);

        for (const { cat, newOrder } of updates) {
          const response = await api.categories.update(cat.categoryId, {
            name: cat.name,
            description: cat.description,
            displayOrder: newOrder,
            isPublic: cat.isPublic,
          });
          if (!response.success) {
            alert('Failed to update order');
            return;
          }
        }
        refetch();
      } catch (err) {
        alert('Failed to update order');
      } finally {
        setIsSubmitting(false);
      }
    },
    [categories, refetch]
  );

  const handleDragEnd = useCallback(() => {
    setDragOverId(null);
    setDraggedId(null);
  }, []);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Categories</h1>
          <p className="text-text-muted mt-1">Organize products into categories</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ New Category'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {showForm && (
        <div className="bg-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-text mb-4">Create Category</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPublic"
                checked={formData.isPublic}
                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="isPublic" className="text-sm font-medium text-text">
                Available on website
              </label>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Category'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-surface rounded-lg shadow overflow-hidden">
        {categories.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            No categories yet. Create one to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase w-12">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Public
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr
                    key={category.categoryId}
                    draggable={editingCategoryId !== category.categoryId}
                    onDragStart={(e) => handleDragStart(e, category.categoryId)}
                    onDragOver={(e) => handleDragOver(e, category.categoryId)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, category.categoryId)}
                    onDragEnd={handleDragEnd}
                    className={`${
                      editingCategoryId !== category.categoryId
                        ? 'cursor-grab active:cursor-grabbing'
                        : ''
                    } ${dragOverId === category.categoryId ? 'bg-blue-50' : ''} ${
                      draggedId === category.categoryId ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-text-muted text-sm">
                      <span className="inline-block" title="Drag to reorder">
                        ⋮⋮
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap align-top">
                      {editingCategoryId === category.categoryId ? (
                        <form
                          onSubmit={(e) => handleUpdate(e, category.categoryId)}
                          className="space-y-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            required
                            value={editFormData.name}
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, name: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="text-sm bg-primary text-white py-1 px-3 rounded hover:bg-blue-700"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="text-sm bg-gray-200 py-1 px-3 rounded hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <span className="font-medium text-text">{category.name}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 align-top">
                      {editingCategoryId === category.categoryId ? (
                        <textarea
                          value={editFormData.description}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, description: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                          rows={3}
                        />
                      ) : (
                        <p className="text-sm text-text-muted">
                          {category.description || <span className="italic">No description</span>}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap align-top">
                      {editingCategoryId === category.categoryId ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`editIsPublic-${category.categoryId}`}
                            checked={editFormData.isPublic}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                isPublic: e.target.checked,
                              })
                            }
                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
                          />
                          <label
                            htmlFor={`editIsPublic-${category.categoryId}`}
                            className="text-sm"
                          >
                            Available on website
                          </label>
                        </div>
                      ) : (
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            category.isPublic
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {category.isPublic ? 'Public' : 'Hidden'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right align-top">
                      {editingCategoryId !== category.categoryId && (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => handleEditClick(category)}
                            className="text-primary hover:text-blue-700 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(category.categoryId)}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
